import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Sentry } from '@/lib';
import {
  useCreateEntry,
  useDeleteEntry,
  useUpdateEntry,
  useSchedulePermissions,
} from '@/pages/Schedule/hooks';
import { useDraftEntryStore } from '@/pages/Schedule/stores';
import type { DecoratedEntry, DecoratedInstance } from '@/pages/Schedule/types';
import {
  isInstanceOnPrimaryFeed,
  validateEntry,
} from '@/pages/Schedule/utils/entry';
import { useToast, useDisclosure } from '@/ui';
import { getErrorMessage, type QueryError } from '@/utils/errors';
import { useScheduleContext } from '../../contexts/ScheduleContext';
import { ConfirmCloseDialog } from './components/ConfirmCloseDialog';
import { EntryModalContextProvider, useEntryModalContext } from './contexts';
import { useSyncUrl } from './hooks/useSyncUrl';
import { FeedEntryModal } from './modals/FeedEntryModal';
import { ReadOnlyEntryModal } from './modals/ReadOnlyEntryModal';
import { WriteableEntryModal } from './modals/WriteableEntryModal';
import type { EntryModalResult } from './types';

type Props = {
  onEntryCreateSuccess?: (entry: DecoratedEntry) => void;
  onEntryUpdateSuccess?: (entry: DecoratedEntry) => void;
  onEntryDeleteSuccess?: (entry: DecoratedEntry) => void;
  onInstanceDeleteSuccess?: (instance: DecoratedInstance) => void;
};

const EntryModalWithContext = ({
  onEntryCreateSuccess,
  onEntryUpdateSuccess,
  onEntryDeleteSuccess,
  onInstanceDeleteSuccess,
}: Props) => {
  const { scheduleId } = useScheduleContext();
  const { hasSchedulePermission } = useSchedulePermissions();
  const { deleteEntry } = useDeleteEntry();
  const { createEntry } = useCreateEntry();
  const { updateEntry, addExclusion } = useUpdateEntry();
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation('schedule');
  const toast = useToast();
  const { isOpen, messagesPanel, activeSidebarPanel } = useEntryModalContext();

  const {
    isOpen: isConfirmCloseDialogOpen,
    onOpen: openConfirmClose,
    onClose: closeConfirmClose,
  } = useDisclosure();

  const {
    draftComposer,
    draftMode,
    draftInstance,
    clearDraftEntry,
    changeDraftMode,
  } = useDraftEntryStore((state) => ({
    draftComposer: state.draftComposer,
    draftMode: state.draftMode,
    draftInstance: state.draftInstance,
    clearDraftEntry: state.clearDraftEntry,
    changeDraftMode: state.changeDraftMode,
  }));

  useSyncUrl(isOpen);

  if (!isOpen) {
    return null;
  }

  const errorToast = (err: QueryError, title: string) => {
    toast.error({
      title,
      description: getErrorMessage(err),
    });
  };

  // Persist the entry before a message is saved.
  // - when adding a message while creating a entry, this ensures that the entry
  //   exists in the database before saving the message.
  // - when adding a message to an existing entry, this ensures that any changes
  //   to an entry's date/time are persisted before the message is attached
  const upsertEntry = async (entry: DecoratedEntry) => {
    const error = validateEntry(entry);
    if (error) {
      toast.error(error.message);
      return Promise.reject(error);
    }

    return new Promise<void>((resolve, reject) => {
      if (draftMode === 'create') {
        createEntry(entry, {
          onError: (err) => {
            errorToast(err, t('errors.creating_entry'));
            reject(err);
          },
          onSuccess: () => {
            onEntryCreateSuccess?.(entry);
            changeDraftMode('edit');
            resolve();
          },
        });
        trackEvent('entry:create', { composer: 'modal' });
      } else if (draftMode === 'edit') {
        updateEntry(entry, {
          onError: (err) => {
            errorToast(err, t('errors.updating_entry'));
            reject(err);
          },
          onSuccess: () => {
            onEntryUpdateSuccess?.(entry);
            resolve();
          },
        });
        trackEvent('entry:update');
      }
    });
  };

  const handleClose = (result: EntryModalResult, entry: DecoratedEntry) => {
    if (result === 'save') {
      const error = validateEntry(entry);
      if (error) {
        toast.error(error.message);
        return;
      }

      if (draftMode === 'create') {
        createEntry(entry, {
          onError: (err) => errorToast(err, t('errors.creating_entry')),
          onSuccess: () => onEntryCreateSuccess?.(entry),
        });
        trackEvent('entry:create', { composer: 'modal' });
      } else if (draftMode === 'edit') {
        updateEntry(entry, {
          onError: (err) => errorToast(err, t('errors.updating_entry')),
          onSuccess: () => onEntryUpdateSuccess?.(entry),
        });
        trackEvent('entry:update');
      }
      clearDraftEntry();
    } else if (result === 'delete-entry') {
      deleteEntry(entry.id, {
        onSuccess: () => onEntryDeleteSuccess?.(entry),
        onError: (err) => errorToast(err, t('errors.deleting_entry')),
      });
      clearDraftEntry();
      trackEvent('entry:delete');
    } else if (result === 'delete-instance' && draftInstance) {
      addExclusion(entry, draftInstance.startDate, {
        onSuccess: () => onInstanceDeleteSuccess?.(draftInstance),
      });
      clearDraftEntry();
      trackEvent('entry:delete-instance');
    } else if (result === 'cancel') {
      clearDraftEntry();
    } else {
      const err = new Error(`Unknown handleEntryModalClose result: ${result}`);
      console.error(err);
      Sentry.captureException(err);
    }
  };

  const handleRequestClose = (
    result: EntryModalResult,
    entry: DecoratedEntry
  ) => {
    if (
      result === 'save' &&
      activeSidebarPanel === 'messages' &&
      messagesPanel?.messageMode === 'create'
    ) {
      openConfirmClose();
    } else {
      handleClose(result, entry);
    }
  };

  const showReadOnlyEntryModal =
    !hasSchedulePermission('ENTRY_CREATE') &&
    !hasSchedulePermission('ENTRY_DELETE') &&
    !hasSchedulePermission('ENTRY_UPDATE');

  return (
    <>
      <ConfirmCloseDialog
        isOpen={isConfirmCloseDialogOpen}
        onClose={closeConfirmClose}
      />

      {showReadOnlyEntryModal ? (
        <ReadOnlyEntryModal onClose={() => clearDraftEntry()} />
      ) : draftInstance &&
        !isInstanceOnPrimaryFeed(draftInstance, scheduleId) ? (
        <FeedEntryModal onClose={handleRequestClose} onSave={upsertEntry} />
      ) : draftComposer === 'modal' ? (
        <WriteableEntryModal
          onClose={handleRequestClose}
          onSave={upsertEntry}
        />
      ) : null}
    </>
  );
};

export const EntryModal = (props: Props) => {
  return (
    <EntryModalContextProvider>
      <EntryModalWithContext {...props} />
    </EntryModalContextProvider>
  );
};
